// =============================================================
// sageImportWatch.js — how the Sage Import screen decides what an upload did.
//
// THE PROCESS RESPONSE IS NOT THE OUTCOME; THE IMPORT ROW IS.
// `POST /api/sage/imports/{id}/process` runs the whole import inline (a GL
// export is tens of thousands of rows, and the run then recomputes the deal
// IRRs and the capital-deployment snapshot before it answers), and it sits
// behind the Static Web Apps edge, which closes a request at ~45s with a
// bodyless 500. So the client cannot wait on that response: it used to be sent
// on the 10s default budget and reported "Upload failed: Request timed out
// after 10s" over an import that was running fine and would land minutes later.
//
// `runImport` writes its progress to `dbo.SageImportLog` — 'Processing' before
// any work, then 'Completed'/'Failed' with the row counts — so the screen
// watches that row and only falls back to the request's own error when the row
// shows the import was never picked up. Same shape as the document-migration
// run: the poller arbitrates, a failed start is recorded rather than shown.
//
// Pure — no React, no api client — so the rules are unit-testable
// (frontend/src/__tests__/sageImportWatch.test.js).
// =============================================================

// How long the POST is given before the client stops waiting on it. Nothing
// enforces this server-side; it only bounds the abandoned fetch, since the
// import row is what the screen actually reads.
export const PROCESS_TIMEOUT_MS = 600000;   // 10 min

// How long the screen narrates an import before handing it to the list's own
// poll, and how often it re-reads the row while it does. NOT a limit on the
// import — nothing here can stop one; the run is server-side and durable.
export const WATCH_MS      = 900000;        // 15 min
export const WATCH_POLL_MS = 4000;

const IN_FLIGHT = ['Pending', 'Processing'];

/** Has the import reached a final state? Anything that is not queued or
 *  running is settled — including 'Reverted', so an unexpected status can
 *  never leave the screen watching forever. */
export function isImportSettled(status) {
  return typeof status === 'string' && status.trim() !== '' && !IN_FLIGHT.includes(status);
}

/** Has the import been picked up at all? A row still 'Pending' after the
 *  request finished means nothing ever ran, which is the one case where the
 *  request's own error is the real answer. */
export function isImportStarted(row) {
  return !!row && row.status !== 'Pending';
}

/**
 * Find one import in a `GET /api/sage/imports` page.
 *
 * Matched case-INSENSITIVELY on purpose: the id comes back from
 * `POST /sage/upload-url` as the lowercase uuid it was generated as, while SQL
 * Server hands the same UNIQUEIDENTIFIER back uppercase — a strict compare
 * finds nothing and the screen would watch an import it can already see.
 * `toCamel` lowercases only the first character, so the column arrives as
 * `importID` from `ImportID` and `importId` from `ImportId`; accept both.
 */
export function findImportRow(rows, importId) {
  if (!Array.isArray(rows) || !importId) return null;
  const want = String(importId).toLowerCase();
  return rows.find(r => String(r?.importId ?? r?.importID ?? '').toLowerCase() === want) || null;
}

/**
 * The message the upload card shows once watching ends.
 *
 * `tone` drives the colour rather than a prefix match on the text, so a wording
 * change cannot silently turn an error green.
 */
export function uploadOutcome({ row, requestError, stillRunning } = {}) {
  if (row && row.status === 'Completed') {
    const inserted = Number(row.rowsSucceeded) || 0;
    const skipped  = Number(row.rowsSkipped)   || 0;
    const done     = `Done — ${inserted} rows inserted, ${skipped} skipped.`;
    // A COMPLETED IMPORT IS NOT NECESSARILY A CLEAN ONE. `runImport` keeps the
    // status 'Completed' when rows landed even if superseding the prior import
    // failed — which leaves duplicate transactions live and doubles every
    // Sage-derived figure. Reporting that green, under a row count that is
    // itself correct, is how it went unnoticed for five days. The reason is on
    // the row; show it rather than the tick.
    if (row.errorSummary) {
      return { tone: 'warn', text: `${done} ${row.errorSummary}` };
    }
    return { tone: 'success', text: done };
  }
  if (row && row.status === 'Failed') {
    return {
      tone: 'error',
      text: `Import failed: ${row.errorSummary || 'no reason recorded — open the import below for details.'}`,
    };
  }
  // Still going when the watch ran out, or an error on a request whose import
  // did start: the row is the truth and it says the work is under way.
  if (stillRunning || isImportStarted(row)) {
    return {
      tone: 'info',
      text: 'Still importing — a large export takes a while. This page updates as it finishes; you can leave it.',
    };
  }
  if (requestError) return { tone: 'error', text: `Upload failed: ${requestError.message || requestError}` };
  return { tone: 'info', text: 'Processing…' };
}
